<template>
  <div>
    <v-container>
        <v-row>
            <v-col cols="12">
                <k-card-header title="Core Management"></k-card-header>
            </v-col>
        </v-row>
    </v-container>

    <v-container v-if="loading">
        <v-progress-circular indeterminate size="50" />
    </v-container>

    <v-container v-if="!loading && configVersions">
        <v-row>
            <v-col cols="12">
            <v-card class="detailCard">
                <v-card-text>
                    <v-container>
                        <v-simple-table>
                            <thead>
                                <tr>
                                    <th scope="col"></th>
                                    <th>Name</th>
                                    <th scope="col">Start Date</th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr v-for="item in configVersions"
                                    :ref="'row'+item.id"
                                    :key="item.id"
                                    :class="item.id===configVersion.id? 'selected-row' : ''">
                                    <td>
                                        <v-btn v-if="item.id"
                                            :ref="'configButton'+item.id"
                                            @click="onConfigureButtonClick(item)"
                                            >Configure
                                        </v-btn>
                                    </td>

                                    <td>
                                        <v-text-field
                                            :class="nonUniqueNames.has(item.name)?
                                                'text-invalid-mod':
                                                (idsOfModifiedNames.has(item.id)?
                                                'text-valid-mod':'text-original')"
                                            :ref="'name'+item.id"
                                            v-model="item.name"
                                            v-on:input="onNameModification(item)"
                                            :rules="[value => !!value || 'Required.']">
                                        </v-text-field>
                                    </td>

                                    <td :class="nonUniqueDates.has(item.startDate)?
                                            'text-invalid-mod':
                                            (idsOfModifiedDates.has(item.id)?
                                            'text-valid-mod':'text-original')">
                                        <text-box-date-picker
                                            :ref="'startDate'+ item.id"
                                            v-model="item.startDate"
                                            v-on:input="onStartDateModification(item)"/>
                                    </td>

                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-container>

                    <v-row v-if="!isValid" justify="center" align="center">
                        <span id="correctError" style="color: red" align="center">
                            Please provide valid and unique values for indicated fields!
                        </span>
                    </v-row>
                </v-card-text>
            </v-card>
            </v-col>
        </v-row>


        <v-row>
            <!-- Create button -->
            <v-col cols="auto" class="mr-auto">
                <v-btn
                    x-large
                    color="primary"
                    ref="createButton"
                    class="pl-16 pr-16"
                    @click="createConfigVersion()"
                    :disabled="createPressed || saving"
                    >Create
                </v-btn>
            </v-col>

            <v-spacer></v-spacer>

            <!-- Cancel button -->
            <v-col cols="auto" class="mr-auto">
                <v-btn
                    x-large
                    color="primary"
                    ref="cancelButton"
                    class="pl-16 pr-16"
                    @click="resetConfigVersions()"
                    :loading="cancelling"
                    :disabled="!isModified || saving"
                    >Cancel
                </v-btn>
            </v-col>

            <!-- Save button -->
            <v-col cols="auto" class="mr-auto">
                <v-btn
                    x-large
                    color="primary"
                    ref="saveButton"
                    class="pl-16 pr-16"
                    @click="saveConfigVersions()"
                    :disabled="!(isModified && isValid)"
                    :loading="saving"
                    >Save
                </v-btn>
            </v-col>
        </v-row>

    </v-container>

  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {
  Component, Inject, Provide, Watch,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import { Mutation } from 'vuex-class';
import { TextBoxDatePicker } from '@kasasa/portal-components';
import { Alert, NoticeClass } from '@kasasa/fbase-components/lib';
import { KCardHeader } from '@kasasa/fbase-components';
import {
  ConfigVersion,
  ConfigVersionResource,
} from '@/api/configversion';

@Component({
  computed: mapState(['fiId', 'configVersion', 'allConfigVersions']),
  components: {
    TextBoxDatePicker,
    KCardHeader,
  },
})
export default class CoreManagement extends Vue {
    @Inject('ConfigVersionResource')
    configVersionResource!: ConfigVersionResource;

    fiId!: string;

    configVersion!: ConfigVersion;

    allConfigVersions!: ConfigVersion[];

    @Mutation('configVersion') mutationConfigVersion: any;

    @Mutation('allConfigVersions') mutationAllConfigVersions: any;

    configVersions: ConfigVersion[] | null = null;

    configVersionsOnLoad: ConfigVersion[] | null = null;

    lookupConfigVersionsOnLoadById = new Map<number|null, ConfigVersion>();

    // progress status
    loading: boolean = false;

    cancelling: boolean = false;

    saving: boolean = false;

    // button enable/disable
    createPressed: boolean = false;

    isModified: boolean = false;

    isValid: boolean = true;


    nonUniqueNames = new Set<string>();

    nonUniqueDates = new Set<string>();

    idsOfModifiedNames = new Set<number|null>();

    idsOfModifiedDates = new Set<number|null>();


    mounted() {
      this.loadConfigVersions();
    }

    @Watch('fiId')
    loadConfigVersions() {
      this.loading = true;
      this.configVersionResource
        .getAll(this.fiId)
        .then((res: ConfigVersion[]) => {
          this.configVersions = res;
          this.sortConfigVersions(this.configVersions!);
          const cloned = this.configVersions.map(x => Object.assign({}, x));
          this.configVersionsOnLoad = cloned;
          this.configVersionsOnLoad.forEach((configVersion) => {
            this.lookupConfigVersionsOnLoadById.set(configVersion.id, configVersion);
          });
        })
        .catch((error) => {
          this.configVersions = [];
          this.configVersionsOnLoad = null;
          this.lookupConfigVersionsOnLoadById = new Map<number|null, ConfigVersion>();

          this.loading = false;
          this.cancelling = false;
          this.saving = false;

          this.createPressed = false;
          this.isModified = false;
          this.isValid = true;

          this.nonUniqueNames.clear();
          this.nonUniqueDates.clear();
          this.idsOfModifiedNames.clear();
          this.idsOfModifiedDates.clear();

          this.showError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    }

    sortConfigVersions = (configVersions: ConfigVersion[]) => configVersions.sort((a, b) => {
      if (new Date(a.startDate).valueOf() < new Date(b.startDate).valueOf()) {
        return 1;
      }
      if (new Date(a.startDate).valueOf() > new Date(b.startDate).valueOf()) {
        return -1;
      }
      throw new Error('Server returned ConfigVersion collection with'
                + ' unexpected startDate unique constraint violation!');
    })

    showError(error: any) {
      if (error.response && error.response.data && error.response.data.entity) {
        this.setError(error.response.data.entity);
      } else {
        this.setError('An unexpected error occurred.');
      }
    }

    setError(message: string) {
      const al = new Alert(message, NoticeClass.ERROR);
      al.setTimeout(5000);
      this.$store.dispatch('notices/add', al);
    }

    createConfigVersion() {
      const newCoreVersion: ConfigVersion = {
        id: null,
        name: 'New Core',
        startDate: new Date().toISOString().substring(0, 10),
      };
        this.configVersions!.push(newCoreVersion);
        this.idsOfModifiedNames.add(newCoreVersion.id);
        this.idsOfModifiedDates.add(newCoreVersion.id);

        this.createPressed = true;
        this.isModified = true;
        this.performNameValidation();
        this.performDateValidation();
    }

    performNameValidation() {
      const uniqueNames = new Set();
      this.nonUniqueNames.clear();
        this.configVersions!.forEach((configVersion) => {
          if (!uniqueNames.has(configVersion.name)
                && !this.nonUniqueNames.has(configVersion.name)) {
            uniqueNames.add(configVersion.name);
          } else if (uniqueNames.has(configVersion.name)) {
            uniqueNames.delete(configVersion.name);
            this.nonUniqueNames.add(configVersion.name);
          }
        });
        this.isValid = this.isDataValid() && !uniqueNames.has('');
    }

    performDateValidation() {
      const uniqueDates = new Set();
      this.nonUniqueDates.clear();
        this.configVersions!.forEach((configVersion) => {
          if (!uniqueDates.has(configVersion.startDate)
                && !this.nonUniqueDates.has(configVersion.startDate)) {
            uniqueDates.add(configVersion.startDate);
          } else if (uniqueDates.has(configVersion.startDate)) {
            uniqueDates.delete(configVersion.startDate);
            this.nonUniqueDates.add(configVersion.startDate);
          }
        });
        this.isValid = this.isDataValid() && !uniqueDates.has('');
    }

    isDataValid():boolean {
      return this.nonUniqueNames.size === 0 && this.nonUniqueDates.size === 0;
    }

    resetConfigVersions() {
      this.cancelling = true;
      const cloned = this.configVersionsOnLoad!.map(x => Object.assign({}, x));
      this.configVersions = cloned;

      this.nonUniqueNames.clear();
      this.nonUniqueDates.clear();
      this.idsOfModifiedNames.clear();
      this.idsOfModifiedDates.clear();

      this.createPressed = false;
      this.isModified = false;
      this.isValid = true;

      this.cancelling = false;
    }

    saveConfigVersions() {
      if (!this.isModified || !this.isValid) {
        return;
      }
      this.saving = true;
      const configVersionsToBeUpdated = this.getConfigVersionsRequiringUpdate();
      const configVersionToBeCreated = this.getConfigVersionRequiringInsert();

      const promises = configVersionsToBeUpdated.map(
        configVersion => this.configVersionResource
          .updateVersion(this.fiId, configVersion)
          .catch((error) => {
            this.showError(error);
          }),
      );

      if (configVersionToBeCreated) {
        promises.push(this.configVersionResource
          .insertVersion(this.fiId, configVersionToBeCreated!)
          .then((result: ConfigVersion) => {
            if (result) { // new cv created
              this.configVersions![this.configVersions!.length - 1].id = result.id;
            }
          })
          .catch((error) => {
            this.showError(error);
          }));
      }

      Promise.all(promises).then(() => {
        this.onSuccessfulSaving();
        this.saving = false;
      });
    }

    onSuccessfulSaving() {
      this.sortConfigVersions(this.configVersions!);
      const cloned = this.configVersions!.map(x => Object.assign({}, x));
      this.configVersionsOnLoad = cloned;
      this.configVersionsOnLoad.forEach((configVersion) => {
        this.lookupConfigVersionsOnLoadById.set(configVersion.id, configVersion);
      });

      this.idsOfModifiedNames.clear();
      this.idsOfModifiedDates.clear();

      this.createPressed = false;
      this.isModified = false;
      this.isValid = true;

      this.onConfigVersionsChanged(this.configVersions!);
    }

    getConfigVersionsRequiringUpdate():ConfigVersion[] {
      const configVersionsToBeUpdated : ConfigVersion[] = [];
      if (!this.configVersionsOnLoad || !this.configVersions) {
        return configVersionsToBeUpdated;
      }
      let i = this.configVersionsOnLoad.length;
      while (i--) {
        if (this.configVersionsOnLoad[i].id === this.configVersions[i].id
            && (this.configVersionsOnLoad[i].name !== this.configVersions[i].name
            || this.configVersionsOnLoad[i].startDate !== this.configVersions[i].startDate)) {
          configVersionsToBeUpdated.push(this.configVersions[i]);
        }
      }
      return configVersionsToBeUpdated;
    }

    getConfigVersionRequiringInsert():ConfigVersion | null {
      let configVersionToBeInserted : ConfigVersion | null = null;
      if (!this.configVersionsOnLoad || !this.configVersions) {
        return configVersionToBeInserted;
      }
      if (this.configVersionsOnLoad.length === this.configVersions.length - 1) {
        configVersionToBeInserted = Object.assign({},
          this.configVersions[this.configVersions.length - 1]);
      }
      return configVersionToBeInserted;
    }

    onConfigureButtonClick(configVersion: ConfigVersion) {
      this.mutationConfigVersion(configVersion);
    }

    onConfigVersionsChanged(configVersions: ConfigVersion[]) {
      this.mutationAllConfigVersions(configVersions);
    }

    onNameModification(configVersion : ConfigVersion) {
      const cv = this.lookupConfigVersionsOnLoadById.get(configVersion.id);
      if (cv && configVersion.name === cv.name) {
        this.idsOfModifiedNames.delete(configVersion.id);
      } else {
        this.idsOfModifiedNames.add(configVersion.id);
      }
      this.isModified = this.isDataModified();
      this.performNameValidation();
    }

    onStartDateModification(configVersion : ConfigVersion) {
      const cv = this.lookupConfigVersionsOnLoadById.get(configVersion.id);
      if (cv && configVersion.startDate === cv.startDate) {
        this.idsOfModifiedDates.delete(configVersion.id);
      } else {
        this.idsOfModifiedDates.add(configVersion.id);
      }
      this.isModified = this.isDataModified();
      this.performDateValidation();
    }

    isDataModified():boolean {
      return this.idsOfModifiedNames.size !== 0 || this.idsOfModifiedDates.size !== 0;
    }
}
</script>

<style lang="scss">
.text-invalid-mod input{
  color: red !important;
}
.text-original input{
  color: black !important;
}
.text-valid-mod input{
  background: skyblue;
}
.selected-row td{
  background: rgb(249, 228, 216);
}
</style>
